import { rootPaths } from '@core/constants/paths';
import { salesFlowRoutes } from '@core/enums/orderflow-paths';
import { Selector } from '@ngxs/store';
import { DealerClaimType } from '../../enums/dealer-claim-type';
import { Dealer, DealerClaim, Seller } from '../../models/dealer.model';
import { DealerState, DealerStateModel } from './dealer.state';

export class DealerStateSelectors {
  @Selector([DealerState])
  static getState(state: DealerStateModel): DealerStateModel {
    return state;
  }

  @Selector([DealerState])
  static isGettingSellers(state: DealerStateModel): boolean {
    return state.isGettingSellers;
  }

  @Selector([DealerState])
  static isGettingDealers(state: DealerStateModel): boolean {
    return state.isGettingDealers;
  }

  @Selector([DealerState])
  static activeDealerClaims(state: DealerStateModel): DealerClaim[] {
    return state.active?.claims.filter(claim => claim.value === 'true') ?? [];
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static isDetailedCreditCheckEnabled(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanSellMobilePhone);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static hasExtendedCreditCheckClaim(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.DiegoExtendedCreditCheckResultFunctionality);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static hasSecretNumberAccess(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.DiegoSecretCustomerInformation);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canSellHardwareWithLogistics(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanSellHardwareWithLogistics);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canSellCellPhoneWithSubscription(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanSellCellPhoneWithSubscription);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canSellCellPhoneWithoutSubscription(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanSellCellPhoneWithoutSubscription);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canSeeAllOrders(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanSeeAllOrders);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canSellMBBSubscriptionOnly(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanSellMBBSubscriptionOnly);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canSellMobilePhone(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanSellMobilePhone);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canReplaceMobilbyttePhone(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanReplaceMobilbyttePhone);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static isEquipmentUpgradeEnabled(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.EquipmentUpgrade);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canAdministerSellers(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanAdministerSellers);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static isAccessoryKitEnabled(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.AccessoryKit);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canSellMobileInsurance(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanSellMobileInsurance);
  }

  @Selector([DealerState])
  static hasDealers(state: DealerStateModel): boolean {
    return state.dealers.length > 0;
  }

  @Selector([DealerState])
  static dealers(state: DealerStateModel): Dealer[] {
    return state.dealers;
  }

  @Selector([DealerState])
  static activeDealer(state: DealerStateModel): Dealer {
    return state.active;
  }

  @Selector([DealerState])
  static activeDealerName(state: DealerStateModel): string {
    return state.active?.dealerName;
  }

  @Selector([DealerState])
  static activeDealerDisplayName(state: DealerStateModel): string {
    return state.active?.displayName;
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static checkedClaims(claims: DealerClaim[]) {
    return (claimsToCheck: DealerClaimType[]): boolean[] =>
      claimsToCheck.map((claimToCheck: DealerClaimType) => {
        const activeClaimNames = claims.map((activeClaim: DealerClaim) => activeClaim.name);
        return activeClaimNames.includes(claimToCheck);
      });
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static hasPhysicalSigningClaim(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.IdentificationWithPhysicalDoc);
  }

  @Selector([DealerStateSelectors.hasPhysicalSigningClaim])
  static canOpenInManny(isRetail: boolean): boolean {
    return !isRetail;
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canUseManualRegistration(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.AllowManualPhoneRegistration);
  }

  @Selector([DealerState])
  static sellers(state: DealerStateModel): Seller[] {
    return state.sellers?.filter(seller => seller.isEnabled) ?? [];
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static isQuestionOnlyVerification(claims: DealerClaim[]): boolean {
    return (
      claims.some(x => x.name === DealerClaimType.IdentificationQuestions) &&
      !claims.find(x => x.name === DealerClaimType.IdentificationWithPhysicalDoc)
    );
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static hasControlQuestionsClaim(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.IdentificationQuestions);
  }

  @Selector([DealerState])
  static isCurrentlySearching(state: DealerStateModel): boolean {
    return state.isCurrentlySearching;
  }

  @Selector([DealerState])
  static searchResult(state: DealerStateModel): Seller[] {
    return state.searchResults;
  }

  @Selector([DealerState])
  static allDealers(state: DealerStateModel): Dealer[] {
    return state.allDealers;
  }

  @Selector([DealerState])
  static isGettingAllDealers(state: DealerStateModel): boolean {
    return state.isGettingAllDealers;
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static hasPaperContractClaim(claims: DealerClaim[]): boolean {
    return claims.some(x => x.name === DealerClaimType.ContractSigningPaper);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static hasSalesCompetitionClaim(claims: DealerClaim[]): boolean {
    return claims.some(x => x.name === DealerClaimType.CanUseSalesCompetition);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static salesFlowRoutes(activeDealerClaims: DealerClaim[]) {
    return [
      ...salesFlowRoutes,
      {
        route: rootPaths.summary,
        text: 'Oppsummering',
        claimType: null,
      },
    ].filter(salesFlowRoute =>
      salesFlowRoute.claimType
        ? activeDealerClaims
            .map((activeDealerClaim: DealerClaim) => activeDealerClaim.name)
            .includes(salesFlowRoute.claimType)
        : salesFlowRoute
    );
  }

  @Selector([DealerState])
  static canMakeSales(state: DealerStateModel): boolean {
    const requiredClaims = new Set([
      DealerClaimType.DiegoVoiceFunctionality,
      DealerClaimType.DiegoMbbFunctionality,
      DealerClaimType.DiegoHardwareFunctionality,
      DealerClaimType.CanSellMobilePhone,
    ]);

    return state.active?.claims.some(claim => requiredClaims.has(claim.name));
  }

  @Selector([DealerState])
  static canOrderBulkSim(state: DealerStateModel): boolean {
    return state.active?.claims.some(claim => claim.name === DealerClaimType.CanOrderBulkSim);
  }

  @Selector([DealerState])
  static canCreateCustomerOnly(state: DealerStateModel): boolean {
    return state.active?.claims.some(claim => claim.name === DealerClaimType.CanCreateCustomerOnly);
  }

  @Selector([DealerState])
  static canRegisterTradeIn(state: DealerStateModel): boolean {
    return state.active?.claims.some(claim => claim.name === DealerClaimType.CanSellTradeIn);
  }

  @Selector([DealerState])
  static isRetailPayment(state: DealerStateModel): boolean {
    return state.active?.claims.some(claim => claim.name === DealerClaimType.RetailPayment);
  }

  @Selector([DealerStateSelectors.canSellCellPhoneWithoutSubscription, DealerStateSelectors.isEquipmentUpgradeEnabled])
  static canOnlySellMobilePhoneWithSubscription(
    canSellCellPhoneWithoutSubscription: boolean,
    isEquipmentUpgradeEnabled: boolean
  ) {
    return !canSellCellPhoneWithoutSubscription && !isEquipmentUpgradeEnabled;
  }

  @Selector([DealerState])
  static canUpdateImei(state: DealerStateModel): boolean {
    return state.active?.claims.some(claim => claim.name === DealerClaimType.CanUpdateImei);
  }

  @Selector([DealerState])
  static canUseBankIdForIdentificationClaim(state: DealerStateModel): boolean {
    return state.active?.claims.some(claim => claim.name === DealerClaimType.CanUseBankIdForIdentification);
  }

  @Selector([DealerState])
  static canBypassBankId(state: DealerStateModel): boolean {
    return state.active?.claims.some(claim => claim.name === DealerClaimType.CanBypassBankId);
  }

  @Selector([DealerStateSelectors.activeDealerClaims])
  static canUseCampaignVouchers(claims: DealerClaim[]): boolean {
    return claims.some(claim => claim.name === DealerClaimType.CanUseCampaignVouchers);
  }

  @Selector([DealerState])
  static canRegisterEidClaim(state: DealerStateModel): boolean {
    return state.active?.claims.some(claim => claim.name === DealerClaimType.CanRegisterEid);
  }
}
